import { Schema, type DOMOutputSpec, type NodeSpec, type MarkSpec } from "prosemirror-model";
import type { ProseMirrorDoc, ScreenplayStyle } from "../types.js";
import { newBlockId } from "../ids.js";

export const BLOCK_STYLES: ScreenplayStyle[] = [
  "sceneHeading",
  "sceneCharacters",
  "action",
  "character",
  "parenthetical",
  "dialogue",
  "lyrics",
  "transition",
  "shot",
  "inlineNote",
  "sequenceHeading",
  "actHeading",
  "beatHeading",
  "unformattedText",
  "pageBreak",
];

const STYLE_CLASS: Record<string, string> = {
  sceneHeading: "sp-scene-heading",
  sceneCharacters: "sp-scene-characters",
  action: "sp-action",
  character: "sp-character",
  parenthetical: "sp-parenthetical",
  dialogue: "sp-dialogue",
  lyrics: "sp-lyrics",
  transition: "sp-transition",
  shot: "sp-shot",
  inlineNote: "sp-inline-note",
  sequenceHeading: "sp-sequence-heading",
  actHeading: "sp-act-heading",
  beatHeading: "sp-beat-heading",
  unformattedText: "sp-unformatted",
};

const blockAttrs = {
  id: { default: null },
  style: { default: "action" },
  revision: { default: "none" },
  sceneNumber: { default: null },
};

const paragraphSpec: NodeSpec = {
  content: "inline*",
  group: "block",
  defining: true,
  attrs: blockAttrs,
  parseDOM: [
    {
      tag: "p[data-style]",
      getAttrs: (dom) => {
        const el = dom as HTMLElement;
        return {
          id: el.getAttribute("data-id"),
          style: el.getAttribute("data-style") ?? "action",
          revision: el.getAttribute("data-revision") ?? "none",
          sceneNumber: el.getAttribute("data-scene-number"),
        };
      },
    },
    {
      tag: "p",
      getAttrs: () => ({ style: "action", revision: "none", id: newBlockId(), sceneNumber: null }),
    },
  ],
  toDOM(node): DOMOutputSpec {
    const { id, style, revision, sceneNumber } = node.attrs as {
      id: string | null;
      style: ScreenplayStyle;
      revision: string;
      sceneNumber: string | null;
    };
    const attrs: Record<string, string> = {
      "data-id": id ?? "",
      "data-style": style,
      class: `sp-block ${STYLE_CLASS[style] ?? `sp-${style}`}`,
    };
    if (revision && revision !== "none") attrs["data-revision"] = revision;
    if (sceneNumber) attrs["data-scene-number"] = sceneNumber;
    return ["p", attrs, 0];
  },
};

const pageBreakSpec: NodeSpec = {
  group: "block",
  atom: true,
  selectable: true,
  attrs: { id: { default: null } },
  parseDOM: [{ tag: "div[data-page-break]" }],
  toDOM(node): DOMOutputSpec {
    return [
      "div",
      {
        class: "sp-page-break",
        "data-page-break": "true",
        "data-id": (node.attrs.id as string) ?? "",
      },
      ["span", { class: "sp-page-break-line" }, "PAGE BREAK"],
    ];
  },
};

const dualSpec: NodeSpec = {
  group: "block",
  content: "dual_column dual_column",
  attrs: { id: { default: null } },
  parseDOM: [{ tag: "div[data-dual]" }],
  toDOM(node): DOMOutputSpec {
    // A single content hole contains both dual_column nodes. They become
    // direct grid items, which keeps ProseMirror's one-contentDOM invariant.
    return [
      "div",
      {
        class: "sp-dual",
        "data-dual": "true",
        "data-id": (node.attrs.id as string) ?? "",
      },
      0,
    ];
  },
};

const dualColumnSpec: NodeSpec = {
  content: "block+",
  attrs: { id: { default: null }, side: { default: "left" } },
  parseDOM: [{ tag: "div[data-dual-col]" }],
  toDOM(node): DOMOutputSpec {
    return [
      "div",
      {
        class: "sp-dual-col sp-dual-col-inner",
        "data-dual-col": (node.attrs.side as string) ?? "left",
      },
      0,
    ];
  },
};

const strong: MarkSpec = {
  parseDOM: [
    { tag: "strong" },
    { tag: "b" },
    { style: "font-weight=bold" },
    { style: "font-weight=700" },
  ],
  toDOM: () => ["strong", 0],
};

const em: MarkSpec = {
  parseDOM: [{ tag: "em" }, { tag: "i" }, { style: "font-style=italic" }],
  toDOM: () => ["em", 0],
};

const underline: MarkSpec = {
  parseDOM: [{ tag: "u" }, { style: "text-decoration=underline" }],
  toDOM: () => ["u", 0],
};

const strike: MarkSpec = {
  parseDOM: [{ tag: "s" }, { tag: "del" }, { style: "text-decoration=line-through" }],
  toDOM: () => ["s", 0],
};

const link: MarkSpec = {
  attrs: { href: {}, title: { default: null } },
  inclusive: false,
  parseDOM: [
    {
      tag: "a[href]",
      getAttrs: (dom) => ({
        href: (dom as HTMLElement).getAttribute("href"),
        title: (dom as HTMLElement).getAttribute("title"),
      }),
    },
  ],
  toDOM: (mark) => ["a", { href: mark.attrs.href, title: mark.attrs.title, rel: "noopener noreferrer" }, 0],
};

const revisionMark: MarkSpec = {
  attrs: {
    kind: {},
    color: { default: "#f0a93b" },
    authorId: { default: null },
    authorName: { default: null },
    createdAt: { default: null },
    // Merge-of-paragraphs metadata: reject restores the original block.
    mergeStyle: { default: null },
    mergeBlockId: { default: null },
    mergeSceneNumber: { default: null },
    mergeSide: { default: null },
    mergeRevision: { default: null },
  },
  inclusive: false,
  parseDOM: [
    {
      tag: "span[data-revision]",
      getAttrs: (dom) => ({
        kind: (dom as HTMLElement).getAttribute("data-revision") ?? "insert",
        color: (dom as HTMLElement).getAttribute("data-revision-color") ?? "#f0a93b",
        authorId: (dom as HTMLElement).getAttribute("data-revision-author-id"),
        authorName: (dom as HTMLElement).getAttribute("data-revision-author"),
        createdAt: (dom as HTMLElement).getAttribute("data-revision-created-at"),
      }),
    },
  ],
  toDOM: (mark) => [
    "span",
    {
      class: `sp-rev sp-rev-${String(mark.attrs.kind)}`,
      "data-revision": String(mark.attrs.kind),
      "data-revision-color": String(mark.attrs.color),
      "data-revision-author-id": mark.attrs.authorId ? String(mark.attrs.authorId) : "",
      "data-revision-author": mark.attrs.authorName ? String(mark.attrs.authorName) : "",
      "data-revision-created-at": mark.attrs.createdAt ? String(mark.attrs.createdAt) : "",
      "data-merge-side": mark.attrs.mergeSide ? String(mark.attrs.mergeSide) : "",
      style: `--revision-color:${String(mark.attrs.color)}`,
      title: mark.attrs.authorName
        ? `${mark.attrs.kind === "delete" ? "Удаление" : mark.attrs.kind === "merge" ? "Склейка абзацев" : "Вставка"} · ${String(mark.attrs.authorName)}`
        : undefined,
    },
    0,
  ],
};

const rangeCommentMark: MarkSpec = {
  attrs: {
    id: {},
    color: { default: "#f0c974" },
    resolved: { default: false },
  },
  inclusive: false,
  parseDOM: [
    {
      tag: "span[data-range-comment-id]",
      getAttrs: (dom) => ({
        id: (dom as HTMLElement).getAttribute("data-range-comment-id"),
        color: (dom as HTMLElement).getAttribute("data-range-color") ?? "#f0c974",
        resolved: (dom as HTMLElement).getAttribute("data-range-resolved") === "true",
      }),
    },
  ],
  toDOM: (mark) => [
    "span",
    {
      class: `sp-comment-range${mark.attrs.resolved ? " is-resolved" : ""}`,
      "data-range-comment-id": String(mark.attrs.id),
      "data-range-color": String(mark.attrs.color),
      "data-range-resolved": mark.attrs.resolved ? "true" : "false",
      style: `--comment-color:${String(mark.attrs.color)}`,
    },
    0,
  ],
};

const noteMark: MarkSpec = {
  attrs: { id: {}, color: { default: "#f0a93b" } },
  inclusive: false,
  parseDOM: [{ tag: "span[data-comment-id]" }],
  toDOM: (mark) => [
    "span",
    { class: "sp-inline-comment", "data-comment-id": mark.attrs.id, style: `--note-color:${mark.attrs.color}` },
    0,
  ],
};

export const screenplaySchema = new Schema({
  nodes: {
    doc: { content: "block+" },
    paragraph: paragraphSpec,
    pageBreak: pageBreakSpec,
    dual: dualSpec,
    dual_column: dualColumnSpec,
    hardBreak: {
      inline: true,
      group: "inline",
      selectable: false,
      parseDOM: [{ tag: "br" }],
      toDOM: () => ["br"],
    },
    text: { group: "inline" },
  },
  marks: {
    strong,
    em,
    underline,
    strike,
    link,
    note: noteMark,
    revision: revisionMark,
    rangeComment: rangeCommentMark,
  },
});

export type ScreenplaySchema = typeof screenplaySchema;

export function paragraphJSON(style: ScreenplayStyle, text: string, extra: Record<string, unknown> = {}) {
  return {
    type: "paragraph",
    attrs: {
      id: (extra.id as string) ?? newBlockId(),
      style,
      revision: (extra.revision as string) ?? "none",
      sceneNumber: (extra.sceneNumber as string | null) ?? null,
    },
    ...(text ? { content: [{ type: "text", text }] } : {}),
  };
}

export function emptyDoc(style: ScreenplayStyle = "sceneHeading"): ProseMirrorDoc {
  return { type: "doc", content: [paragraphJSON(style, "")] };
}

export function docFromJSON(json: ProseMirrorDoc) {
  return screenplaySchema.nodeFromJSON(json);
}

export function firstBlockId(doc: ProseMirrorDoc): string | null {
  const first = doc.content?.find((n) => n.type === "paragraph" || n.type === "dual");
  return (first?.attrs?.id as string) ?? null;
}
